import type { RuntimeKind } from '../types';
import { RUNTIME_CAPABILITIES } from '../capabilities';
import { PlatformCapabilityUnsupportedError } from '../errors';
import { createElectronEscPosBackend } from '../electron/escpos-print.electron';
import { createElectronStarPrintBackend } from '../electron/star-print.electron';
import type {
  PlatformPrinter,
  StarPrintBackend,
  EscPosLanBackend,
  StarDiscoverOptions,
  PrinterAvailability,
} from './types';

/**
 * PlatformPrinter (PLAN §5.4).
 *
 * `star-native` and `mock` both resolve to the Capacitor `StarPrint` proxy —
 * that IS today's behaviour on native and web respectively, and the proxy is
 * imported dynamically so the plugin stays out of the boot graph.
 *
 * `star-electron-lan` resolves to the **Star LAN backend over TCP 9100 in the
 * Electron main process** (T3.D). It is the printing half of hazard F2: under
 * Model B the plugin has no `electron` key, so `registerPlugin` would hand a
 * desktop register `StarPrintWeb` — an in-memory mock that reports
 * "Mock LAN mC-Print3" and resolves every print as a success. A register that
 * believes it printed a receipt it never printed is worse than one that cannot
 * print.
 *
 * **The dynamic import below is the mechanism that keeps the mock unreachable.**
 * It runs only for `star-native` and `mock`, so on Electron the plugin module
 * is never evaluated, never calls `registerPlugin`, and therefore never
 * instantiates `StarPrintWeb` — observable from a real Electron window as
 * `Capacitor.Plugins.StarPrint === undefined`
 * (`tests/electron/f2-star-print-mock.spec.ts`).
 */

/** Electron offers LAN only: no Bluetooth/BLE/USB transports on desktop. */
const ELECTRON_INTERFACES = ['lan'] as const;
const NATIVE_INTERFACES = ['lan', 'bluetooth', 'ble', 'usb'] as const;

async function capacitorStarPrintBackend(
  kind: 'star-native' | 'mock'
): Promise<StarPrintBackend> {
  const { StarPrint } = await import('@am3nd-org/capacitor-starprint-plugin');
  return {
    kind,
    supportedInterfaces: NATIVE_INTERFACES,
    /**
     * The plugin's `isNativeAvailable()` lives **here and nowhere else**: this
     * is the only backend on which its `platform: 'android' | 'ios' | 'web'`
     * answer is truthful. Everything above consumes `PrinterAvailability`, so
     * no caller has to know the plugin's union exists.
     */
    describeAvailability: async (): Promise<PrinterAvailability> => {
      const native = await StarPrint.isNativeAvailable();
      if (kind === 'mock') {
        return {
          available: false,
          backend: 'mock',
          starSdkIntegrated: native.starSdkIntegrated,
          message: 'This is a browser build; printing is simulated.',
        };
      }
      return {
        available: native.available,
        backend: 'star-native',
        starSdkIntegrated: native.starSdkIntegrated,
        message: native.message,
      };
    },
    discoverPrinters: (options?: StarDiscoverOptions) =>
      // `knownAddresses` / `sweepSubnet` are desktop-only inputs; the plugin
      // ignores unknown keys, and passing them through keeps one call shape.
      StarPrint.discoverPrinters(options),
    getBluetoothAuthorization: (...args) => StarPrint.getBluetoothAuthorization(...args),
    connect: (...args) => StarPrint.connect(...args),
    disconnect: (...args) => StarPrint.disconnect(...args),
    getStatus: (...args) => StarPrint.getStatus(...args),
    printReceipt: (...args) => StarPrint.printReceipt(...args),
    printImage: (...args) => StarPrint.printImage(...args),
    sendRawCommands: (...args) => StarPrint.sendRawCommands(...args),
    openCashDrawer: (...args) => StarPrint.openCashDrawer(...args),
    addListener: (...args) => StarPrint.addListener(...args),
    removeAllListeners: (...args) => StarPrint.removeAllListeners(...args),
  };
}

export function createPlatformPrinter(kind: RuntimeKind): PlatformPrinter {
  const capabilities = RUNTIME_CAPABILITIES[kind];
  const { thermalPrinting } = capabilities;
  const supportedInterfaces =
    thermalPrinting === 'star-electron-lan' ? ELECTRON_INTERFACES : NATIVE_INTERFACES;

  return {
    thermalPrinting,
    supportedInterfaces,
    mockControlsEnabled: thermalPrinting === 'mock',
    desktopPrintDialog: capabilities.desktopPrintDialog,
    bluetoothPeripherals: capabilities.bluetoothPeripherals,
    escPosLanPrinting: capabilities.escPosLanPrinting,
    escPosAttachedPrinting: capabilities.escPosAttachedPrinting,
    resolveEscPosBackend: async (): Promise<EscPosLanBackend> => {
      // Only the desktop shell has a raw TCP transport. Everywhere else this
      // is a typed refusal, never a mock that "prints" (hazard F2).
      if (!capabilities.escPosLanPrinting) {
        throw new PlatformCapabilityUnsupportedError('escPosLanPrinting', kind);
      }
      return createElectronEscPosBackend();
    },
    resolveBackend: async (): Promise<StarPrintBackend> => {
      if (thermalPrinting === 'star-native' || thermalPrinting === 'mock') {
        return capacitorStarPrintBackend(thermalPrinting);
      }
      if (thermalPrinting === 'star-electron-lan') {
        // The Star LAN backend. Throws `ElectronBridgeUnavailableError` with no
        // shell, which is the fail-closed answer — never the plugin's mock.
        return createElectronStarPrintBackend();
      }
      throw new PlatformCapabilityUnsupportedError('thermalPrinting', kind);
    },
  };
}
